import React, { useState } from 'react';
import {
  Button,
  Card,
  Drawer,
  notification,
  PageHeader,
  Space,
  Spin,
  Statistic,
  Tabs,
  Tag,
} from 'antd';
import { useParams } from 'react-router-dom';
import ChangeVehicleStatus from '../containers/ChangeVehicleStatus';
import ViewVehicleDetails from '../containers/ViewVehicleDetails';
import EditVehicleForm from '../containers/EditVehicleForm';
import ViewVehicleSummery from '../containers/ViewVehicleSummery';
import { convertToCurrencyString } from '../../../utils/currencyUtil';
import {
  useGetVehicleAuctionHistoryQuery,
  useGetVehicleQuery,
  useSortImagesMutation,
  useUpdateVehicleMutation,
} from '../../../app/api/vehicleApiSlice';
import Spinner from '../../../components/common/Spinner';
import ViewVehiclePhotos from '../containers/ViewVehiclePhotos';
import BackButton from '../../../components/common/BackButton';
import VehicleStatus from '../../../core/enums/VehicleStatus';
import VehicleStatusColorCode from '../../../core/enums/VehicleStatusColorCode';
import ViewVehicleInspection from '../containers/ViewInspectionReport';
import ViewVehicleAuctionHistory from '../containers/ViewVehicleAuctionHistory';
import VehicleFile from '../../../core/models/VehicleFile';
import { LoadingOutlined } from '@ant-design/icons';

interface IProps {
  children?: React.ReactNode;
  extra?: React.ReactNode;
}

const Content: React.FC<IProps> = ({ children, extra }: IProps) => (
  <div className='content'>
    <div className='main'>{children}</div>
    <div className='extra'>{extra}</div>
  </div>
);

const ViewVehicle: React.FC = () => {
  const { vehicleId } = useParams<{ vehicleId: any }>();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const { data: vehicle, isLoading } = useGetVehicleQuery(vehicleId);
  const { data: vehicleAuctionHistory } = useGetVehicleAuctionHistoryQuery(vehicleId);
  const [sortImages, { isLoading: isSortImagesLoading, isError: isSortImagesError }] =
    useSortImagesMutation();
  const [updateVehicle, { isError: isUpdateVehicleError }] = useUpdateVehicleMutation();

  const extraContent = (
    <Space direction='vertical'>
      <Space
        style={{
          display: 'flex',
          width: 'max-content',
          justifyContent: 'flex-end',
        }}
      >
        <Statistic
          title='Starting Price'
          value={vehicle ? convertToCurrencyString(vehicle.startingPrice) : 'N/A'}
          style={{ marginRight: 32 }}
        />
        <Statistic
          title='Reserved Price'
          value={vehicle ? convertToCurrencyString(vehicle.reservePrice) : 'N/A'}
          style={{ marginRight: 32 }}
        />
      </Space>
      <Space
        style={{
          display: 'flex',
          width: 'max-content',
          justifyContent: 'flex-end',
        }}
      >
        <Statistic
          title='Sold Price'
          value={vehicle?.purchase ? convertToCurrencyString(vehicle?.purchase.soldPrice) : 'N/A'}
          style={{ marginRight: 32 }}
        />
        <Statistic
          title='Highest Bid'
          value={
            vehicle?.currentBid ? convertToCurrencyString(vehicle?.currentBid.bidPrice) : 'N/A'
          }
          style={{ marginRight: 32 }}
        />
        <Statistic
          title='Buyer'
          value={vehicle?.purchase ? vehicle?.purchase.buyer : 'N/A'}
          style={{ marginRight: 32 }}
        />
      </Space>
    </Space>
  );

  if (isLoading) {
    return <Spinner />;
  }

  if (!vehicle) {
    return <div>Vehicle not found!</div>;
  }

  const sortVehicleImages = (images: VehicleFile[]): void => {
    const data = { ...vehicle, sellerId: vehicle.seller?.id };
    data.images = images;

    sortImages(data)
      .unwrap()
      .then((response) => {
        console.log(response);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <>
      <BackButton />
      <PageHeader
        className='site-page-header-responsive'
        title={vehicle?.name}
        tags={
          <Tag color={vehicle ? VehicleStatusColorCode[vehicle?.status] : ''}>
            {vehicle ? VehicleStatus[vehicle?.status] : 'N/A'}
          </Tag>
        }
        extra={[<ChangeVehicleStatus vehicle={vehicle} key='changeVehicleStatus' />]}
        footer={
          <Tabs
            defaultActiveKey='details'
            size='small'
            type='card'
            items={[
              {
                label: 'Vehicle Details',
                key: 'details',
                children: (
                  <Card>
                    <ViewVehicleDetails
                      layout='vertical'
                      vehicle={vehicle}
                      onEditClick={() => setDrawerVisible(true)}
                    />
                    <Drawer
                      title='Update Vehicle Details'
                      width={600}
                      onClose={() => setDrawerVisible(false)}
                      open={drawerVisible}
                      bodyStyle={{ paddingBottom: 80 }}
                      destroyOnClose={true}
                      extra={
                        <Space>
                          <Button onClick={() => setDrawerVisible(false)}>Cancel</Button>
                          <Button
                            type='primary'
                            form='updateVehicleForm'
                            key='submit'
                            htmlType='submit'
                          >
                            Submit
                          </Button>
                        </Space>
                      }
                    >
                      <EditVehicleForm
                        vehicle={vehicle}
                        onSuccessfulSubmit={() => setDrawerVisible(false)}
                        drawerVisible={drawerVisible}
                      />
                    </Drawer>
                  </Card>
                ),
              },
              {
                label: 'Vehicle Photos',
                key: 'photos',
                children: (
                  <Card>
                    {isSortImagesLoading && (
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                        }}
                      />
                    )}
                    <ViewVehiclePhotos
                      vehicleId={vehicle.id}
                      images={vehicle.images}
                      sortable={true}
                      onSortEnd={(images: VehicleFile[] | undefined) => {
                        if (vehicle && images) {
                          sortVehicleImages(images);
                        }
                      }}
                    />
                  </Card>
                ),
              },
              {
                label: 'Grading Report',
                key: 'inspection',
                children: (
                  <Card>
                    <ViewVehicleInspection vehicle={vehicle} />
                  </Card>
                ),
              },
              {
                label: 'Auction History',
                key: 'auction history',
                children: (
                  <Card>
                    <ViewVehicleAuctionHistory vehicleAuctionHistory={vehicleAuctionHistory} />
                  </Card>
                ),
              },
            ]}
          ></Tabs>
        }
      >
        <Content extra={extraContent}>
          <ViewVehicleSummery column={1} vehicle={vehicle} />
        </Content>
      </PageHeader>
    </>
  );
};

export default ViewVehicle;
